import { weakMapMemoize } from '@reduxjs/toolkit';
import {
  PageDataResourceDefinitions,
  PageVariableDefinitionGroups,
  VariableDefinition,
  View,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { produce } from 'immer';
import { remove } from 'lodash';

// This impelmentation is based on the old `updateDolanVariables` from AngularJS.
const getRefreshedViewVariables = weakMapMemoize(
  (variables: PageVariableDefinitionGroups | undefined, dataResources: PageDataResourceDefinitions | undefined) =>
    produce(variables || [], (variablesDraft) => {
      // NOTE: This implementation was moved from removed `DolanVariables` AngularJS service
      // make sure parameters exists
      const parameters = variablesDraft.filter(({ name }) => name === 'parameters');
      if (parameters.length === 0) {
        variablesDraft.push({ name: 'parameters', items: [] });
      }

      const pageVariables = variablesDraft.filter(({ name }) => name === 'page');
      if (pageVariables.length === 0) {
        variablesDraft.push({ name: 'page', items: [] });
      }

      // fill in datas
      if (dataResources != null) {
        // make sure data exists
        if (!variablesDraft.some(({ name }) => name === 'data')) {
          variablesDraft.push({ name: 'data', items: [] });
        }

        const datas = variablesDraft.filter(({ name }) => name === 'data')[0];
        // remove readOnlys
        datas.items = datas.items.filter((item) => {
          if (item.readOnly) {
            return false;
          }
          return true;
        });

        // fill in missing data schemas, remove readOnly ones that might have changed
        const existingReferenceSchemas = datas.items.map(({ name }) => name);

        // generate readOnly data defs again
        if (dataResources.length) {
          dataResources
            .filter((res) => !Array.from(existingReferenceSchemas).includes(res.reference_name))
            .forEach(({ reference_name, action_name }) => {
              const obj: VariableDefinition = {
                name: reference_name,
                readOnly: true,
                schema:
                  action_name === 'data.findAll'
                    ? {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {},
                        },
                      }
                    : {
                        type: 'object',
                        properties: {},
                      },
              };
              return datas.items.push(obj);
            });
        }

        // remove readOnly nonexisting
        remove(datas.items, (item) => {
          if (item.readOnly) {
            const needle = item.name;
            return !dataResources.some(({ reference_name }) => reference_name === needle);
          }
          return false;
        });
      }
    }),
);

/**
 * Ensures that all data variables in the given DOLAN page structure are up-to-date.
 * Returns the updated page, and utilizes caching so that it is fast to call multiple times
 * for the same view.
 *
 * This impelmentation is based on the old `updateDolanVariables` from AngularJS.
 */
export default function refreshViewVariables(view: View): View {
  const refreshedVariables = getRefreshedViewVariables(view.variables, view.data?.resources);
  if (view.variables !== refreshedVariables) {
    return { ...view, variables: refreshedVariables };
  }
  return view;
}
